import React from 'react';

interface CarrierQuote {
  carrier: string;
  base_premium: number;
  annual_premium: number;
  coverage_limit: number;
  deductible: number;
}

interface PricingInfo {
  facility_id: string;
  tank_count: number;
  risk_score: number;
  quotes: CarrierQuote[];
  summary: {
    best_carrier: string;
    lowest_premium: number;
    savings_vs_highest: number;
    average_premium: number;
  };
}

interface PricingDisplayProps {
  pricingInfo: PricingInfo;
}

const PricingDisplay: React.FC<PricingDisplayProps> = ({ pricingInfo }) => {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };


  const getCarrierColor = (carrier: string): string => {
    const colors: { [key: string]: string } = {
      'Liberty Mutual': '#0066cc',
      'Travelers': '#e31937',
      'Chubb': '#8b0000',
      'AIG': '#005a8b'
    };
    return colors[carrier] || '#6b7280';
  };

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <h3 style={{
        marginTop: 0,
        color: '#1f2937',
        borderBottom: '2px solid #10b981',
        paddingBottom: '0.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        💰 Insurance Pricing Analysis
      </h3>

      {/* Summary Card */}
      <div style={{
        backgroundColor: '#f0fdf4',
        border: '2px solid #10b981',
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>Best Carrier</div>
            <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#059669' }}>
              {pricingInfo.summary.best_carrier}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>Lowest Premium</div>
            <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#059669' }}>
              {formatCurrency(pricingInfo.summary.lowest_premium)}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>Potential Savings</div>
            <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#dc2626' }}>
              {formatCurrency(pricingInfo.summary.savings_vs_highest)}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>Average Premium</div>
            <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#4b5563' }}>
              {formatCurrency(pricingInfo.summary.average_premium)}
            </div>
          </div>
        </div>
      </div>

      {/* Risk Score Info */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#fafafa',
        padding: '0.75rem',
        borderRadius: '4px',
        marginBottom: '1rem'
      }}>
        <span style={{ fontWeight: '500', color: '#374151' }}>
          Facility Risk Score:
        </span>
        <span style={{
          fontWeight: '600',
          color: pricingInfo.risk_score <= 3 ? '#059669' :
                pricingInfo.risk_score <= 6 ? '#d97706' : '#dc2626',
          fontSize: '1.125rem'
        }}>
          {pricingInfo.risk_score}/10
        </span>
      </div>

      {/* Carrier Quotes Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1rem'
      }}>
        {pricingInfo.quotes.map((quote, index) => {
          const isLowest = quote.annual_premium === pricingInfo.summary.lowest_premium;
          return (
            <div
              key={quote.carrier}
              style={{
                border: isLowest ? '2px solid #10b981' : '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '1rem',
                backgroundColor: isLowest ? '#f0fdf4' : 'white',
                position: 'relative'
              }}
            >
              {isLowest && (
                <div style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '12px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  BEST RATE
                </div>
              )}

              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '0.75rem'
              }}>
                <div
                  style={{
                    width: '4px',
                    height: '40px',
                    backgroundColor: getCarrierColor(quote.carrier),
                    marginRight: '0.75rem',
                    borderRadius: '2px'
                  }}
                />
                <div>
                  <h4 style={{
                    margin: 0,
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: '#1f2937'
                  }}>
                    {quote.carrier}
                  </h4>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Annual Premium:</span>
                  <span style={{ fontWeight: '600', color: isLowest ? '#059669' : '#1f2937' }}>
                    {formatCurrency(quote.annual_premium)}
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Base Premium:</span>
                  <span style={{ color: '#4b5563' }}>
                    {formatCurrency(quote.base_premium)}
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Coverage Limit:</span>
                  <span style={{ color: '#4b5563' }}>
                    {formatCurrency(quote.coverage_limit)}
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Deductible:</span>
                  <span style={{ color: '#4b5563' }}>
                    {formatCurrency(quote.deductible)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{
        marginTop: '1rem',
        fontSize: '0.75rem',
        color: '#6b7280',
        textAlign: 'center',
        fontStyle: 'italic'
      }}>
        * Pricing based on facility risk assessment and current market rates. Contact carriers for detailed quotes.
      </div>
    </div>
  );
};

export default PricingDisplay;